import { gql } from '@apollo/client';
import {
  CreatorFields,
  DatePickerFieldsFragment,
  DateRangeFieldsFragment,
  LogicalOperatorFragment,
  PartitionValueFieldsFragment,
  RelationalOperatorFragment,
  SchemaFieldsFragment,
  FunnelStepBriefInfoFields,
  SubjectFragment,
  UserAliasReplacementFieldsFragment,
  VersionFieldsFragment,
} from '../fragments';
import { FilterFragment } from '../filters';
import { MeasureFragment } from '../measures';
import { EventConditionFieldsFragment } from '../events/eventConditions';
import { EntityCategoryFragment } from '../entityCategories';
import { FunnelModeSliceFieldsFragment } from '../fragments';

export const UserAliasQueryCohortFragment = gql`
  fragment UserAliasQueryCohortFields on UserAliasQueryCohort {
    aliasId
  }
`;

export const PercentileFilterFragment = gql`
  fragment PercentileFilterFields on PercentileFilter {
    operator {
      ...RelationalOperatorFields
    }
    values
  }
  ${RelationalOperatorFragment}
`;

export const TimeWindowFragment = gql`
  fragment TimeWindowFields on TimeWindow {
    name
    key
  }
`;

export const SimpleCohortTypeFragment = gql`
  fragment SimpleCohortTypeFields on SimpleCohortType {
    name
    key
  }
`;

export const ExpressionCohortOperatorFragment = gql`
  fragment ExpressionCohortOperatorFields on ExpressionCohortOperator {
    expressionKey: key
    name
  }
`;

export const FunnelCohortOperatorFragment = gql`
  fragment FunnelCohortOperatorFields on FunnelCohortOperator {
    funnelKey: key
    name
  }
`;

export const TimeToConvertOperatorFragment = gql`
  fragment TimeToConvertOperatorFields on TimeToConvertOperator {
    key
    name
  }
`;

export const TimeToConvertConfigFragment = gql`
  fragment TimeToConvertConfigFields on TimeToConvertConfig {
    timeUnit
    values
    operator {
      ...TimeToConvertOperatorFields
    }
  }
  ${TimeToConvertOperatorFragment}
`;

export const TimeToConvertCohortConfigFragment = gql`
  fragment TimeToConvertCohortConfigFields on TimeToConvertCohortConfig {
    timeUnits
    operators {
      ...TimeToConvertOperatorFields
    }
  }
  ${TimeToConvertOperatorFragment}
`;

export const MeasureCohortOperatorFragment = gql`
  fragment MeasureCohortOperatorFields on MeasureCohortOperator {
    measureKey: key
    name
  }
`;

export const PerformedCohortOperatorFragment = gql`
  fragment PerformedCohortOperatorFields on PerformedCohortOperator {
    performedKey: key
    name
  }
`;

export const RetentionCohortOperatorFragment = gql`
  fragment RetentionCohortOperatorFields on RetentionCohortOperator {
    retentionKey: key
    name
  }
`;

// TODO: feature analysis is IExpression type... is it important?
export const ExpressionCohortFragment = gql`
  fragment ExpressionCohortFields on ExpressionCohort {
    analysis {
      id
      display
      ... on Expression {
        pref {
          aliasReplacements {
            ...UserAliasReplacementFields
          }
        }
        query {
          measure {
            ...MeasureFields
          }
        }
      }
    }
    createdAt
    creator {
      ...CreatorFields
    }
    measureId
    filter {
      ...FilterFields
    }
    id
    name
    operator {
      ...ExpressionCohortOperatorFields
    }
    operatorValues
    percentileFilter {
      ...PercentileFilterFields
    }
    schema {
      ...SchemaFields
    }
    subject {
      ...SubjectFields
    }
    timeWindow {
      ...TimeWindowFields
    }
    timeWindowValues
    type {
      ...SimpleCohortTypeFields
    }
    userAlias {
      ...UserAliasQueryCohortFields
    }
    version {
      ...VersionFields
    }
    categories {
      ...EntityCategoryFields
    }
    datePicker {
      ...DatePickerFields
    }
    isFavorite
    isManaged
  }
  ${CreatorFields}
  ${FilterFragment}
  ${PercentileFilterFragment}
  ${ExpressionCohortOperatorFragment}
  ${SchemaFieldsFragment}
  ${SubjectFragment}
  ${TimeWindowFragment}
  ${SimpleCohortTypeFragment}
  ${UserAliasQueryCohortFragment}
  ${VersionFieldsFragment}
  ${EntityCategoryFragment}
  ${MeasureFragment}
  ${UserAliasReplacementFieldsFragment}
  ${DatePickerFieldsFragment}
`;

export const FunnelCohortAnalysisQueryFragment = gql`
  fragment FunnelCohortAnalysisQueryFields on FunnelQuery {
    mode {
      key
    }
    partitions {
      ...PartitionValueFields
    }
    timeUnit
    steps {
      ...FunnelStepBriefInfoFields
    }
    modeSlices {
      ...FunnelModeSliceFields
    }
  }
  ${PartitionValueFieldsFragment}
  ${FunnelStepBriefInfoFields}
  ${FunnelModeSliceFieldsFragment}
`;

export const RetentionCohortAnalysisQueryFragment = gql`
  fragment RetentionCohortAnalysisQueryFields on RetentionQuery {
    attribution {
      key
    }
    retentionUnit
  }
  ${PartitionValueFieldsFragment}
`;

export const FunnelCohortFragment = gql`
  fragment FunnelCohortFields on FunnelCohort {
    analysis {
      id
      display
      pref {
        aliasReplacements {
          ...UserAliasReplacementFields
        }
      }
      query {
        steps {
          name
          seq
          userAlias {
            aliasId
          }
        }
        ...FunnelCohortAnalysisQueryFields
      }
    }
    createdAt
    creator {
      ...CreatorFields
    }
    filter {
      ...FilterFields
    }
    id
    name
    operator {
      ...FunnelCohortOperatorFields
    }
    # Reason for aliasing:
    # other cohort types has different output for operatorValues
    funnelOperatorValues: operatorValues
    schema {
      ...SchemaFields
    }
    subject {
      ...SubjectFields
    }
    timeWindow {
      ...TimeWindowFields
    }
    timeWindowValues
    type {
      ...SimpleCohortTypeFields
    }
    userAlias {
      ...UserAliasQueryCohortFields
    }
    version {
      ...VersionFields
    }
    categories {
      ...EntityCategoryFields
    }
    datePicker {
      ...DatePickerFields
    }
    isFavorite
    isManaged
    durationStartDate
    lookForwardWindow
    timeToConvert {
      ...TimeToConvertConfigFields
    }
  }
  ${CreatorFields}
  ${FilterFragment}
  ${FunnelCohortOperatorFragment}
  ${SchemaFieldsFragment}
  ${SubjectFragment}
  ${TimeWindowFragment}
  ${SimpleCohortTypeFragment}
  ${UserAliasQueryCohortFragment}
  ${VersionFieldsFragment}
  ${EntityCategoryFragment}
  ${FunnelCohortAnalysisQueryFragment}
  ${UserAliasReplacementFieldsFragment}
  ${DatePickerFieldsFragment}
  ${TimeToConvertConfigFragment}
`;

export const MeasureCohortFragment = gql`
  fragment MeasureCohortFields on MeasureCohort {
    createdAt
    creator {
      ...CreatorFields
    }
    filter {
      ...FilterFields
    }
    id
    measure {
      ...MeasureFields
    }
    name
    operator {
      ...MeasureCohortOperatorFields
    }
    operatorValues
    percentileFilter {
      ...PercentileFilterFields
    }
    schema {
      ...SchemaFields
    }
    subject {
      ...SubjectFields
    }
    timeWindow {
      ...TimeWindowFields
    }
    timeWindowValues
    type {
      ...SimpleCohortTypeFields
    }
    userAlias {
      ...UserAliasQueryCohortFields
    }
    version {
      ...VersionFields
    }
    categories {
      ...EntityCategoryFields
    }
    datePicker {
      ...DatePickerFields
    }
    isFavorite
    isManaged
  }
  ${CreatorFields}
  ${FilterFragment}
  ${MeasureFragment}
  ${MeasureCohortOperatorFragment}
  ${PercentileFilterFragment}
  ${SchemaFieldsFragment}
  ${SubjectFragment}
  ${TimeWindowFragment}
  ${SimpleCohortTypeFragment}
  ${UserAliasQueryCohortFragment}
  ${VersionFieldsFragment}
  ${EntityCategoryFragment}
  ${DatePickerFieldsFragment}
`;

export const PerformedCohortFragment = gql`
  fragment PerformedCohortFields on PerformedCohort {
    createdAt
    creator {
      ...CreatorFields
    }
    eventConditions {
      ...EventConditionFields
    }
    filter {
      ...FilterFields
    }
    id
    name
    operator {
      ...PerformedCohortOperatorFields
    }
    # Reason for aliasing:
    # other cohort types has different output for operatorValues
    performedOperatorValues: operatorValues
    schema {
      ...SchemaFields
    }
    subject {
      ...SubjectFields
    }
    timeWindow {
      ...TimeWindowFields
    }
    timeWindowValues
    type {
      ...SimpleCohortTypeFields
    }
    userAlias {
      ...UserAliasQueryCohortFields
    }
    version {
      ...VersionFields
    }
    categories {
      ...EntityCategoryFields
    }
    datePicker {
      ...DatePickerFields
    }
    isFavorite
    isManaged
  }
  ${CreatorFields}
  ${EventConditionFieldsFragment}
  ${FilterFragment}
  ${PerformedCohortOperatorFragment}
  ${SchemaFieldsFragment}
  ${SubjectFragment}
  ${TimeWindowFragment}
  ${SimpleCohortTypeFragment}
  ${UserAliasQueryCohortFragment}
  ${VersionFieldsFragment}
  ${EntityCategoryFragment}
  ${DatePickerFieldsFragment}
`;

export const RetentionCohortFragment = gql`
  fragment RetentionCohortFields on RetentionCohort {
    analysis {
      id
      display
      ... on Retention {
        query {
          retentionUnit
          customTimeBrackets
          ...RetentionCohortAnalysisQueryFields
        }
      }
      timeUnit
    }
    createdAt
    creator {
      ...CreatorFields
    }
    filter {
      ...FilterFields
    }
    id
    name
    operator {
      ...RetentionCohortOperatorFields
    }
    # Reason for aliasing:
    # other cohort types has different output for operatorValues
    retentionOperatorValues: operatorValues
    returnedOperator {
      ...RelationalOperatorFields
    }
    schema {
      ...SchemaFields
    }
    subject {
      ...SubjectFields
    }
    timeWindow {
      ...TimeWindowFields
    }
    timeWindowValues
    type {
      ...SimpleCohortTypeFields
    }
    userAlias {
      ...UserAliasQueryCohortFields
    }
    version {
      ...VersionFields
    }
    categories {
      ...EntityCategoryFields
    }
    datePicker {
      ...DatePickerFields
    }
    isFavorite
    isManaged
    durationStartDate
    lookForwardWindow
  }
  ${CreatorFields}
  ${FilterFragment}
  ${RetentionCohortOperatorFragment}
  ${RelationalOperatorFragment}
  ${SchemaFieldsFragment}
  ${SubjectFragment}
  ${TimeWindowFragment}
  ${SimpleCohortTypeFragment}
  ${UserAliasQueryCohortFragment}
  ${VersionFieldsFragment}
  ${EntityCategoryFragment}
  ${RetentionCohortAnalysisQueryFragment}
  ${DatePickerFieldsFragment}
`;

export const SimpleCohortFragment = gql`
  fragment SimpleCohortFields on SimpleCohort {
    ... on ExpressionCohort {
      ...ExpressionCohortFields
    }
    ... on FunnelCohort {
      ...FunnelCohortFields
    }
    ... on MeasureCohort {
      ...MeasureCohortFields
    }
    ... on PerformedCohort {
      ...PerformedCohortFields
    }
    ... on RetentionCohort {
      ...RetentionCohortFields
    }
  }
  ${ExpressionCohortFragment}
  ${FunnelCohortFragment}
  ${MeasureCohortFragment}
  ${PerformedCohortFragment}
  ${RetentionCohortFragment}
`;

export const CompoundCohortFragment = gql`
  fragment CompoundCohortFields on CompoundCohort {
    createdAt
    creator {
      ...CreatorFields
    }
    id
    logicOps {
      ...LogicalOperatorFields
    }
    name
    schema {
      ...SchemaFields
    }
    userAlias {
      ...UserAliasQueryCohortFields
    }
    version {
      ...VersionFields
    }
    simpleCohorts {
      ...SimpleCohortFields
    }
    categories {
      ...EntityCategoryFields
    }
    isFavorite
    isManaged
  }
  ${CreatorFields}
  ${LogicalOperatorFragment}
  ${SchemaFieldsFragment}
  ${UserAliasQueryCohortFragment}
  ${VersionFieldsFragment}
  ${SimpleCohortFragment}
  ${EntityCategoryFragment}
`;

export const CohortFragment = gql`
  fragment CohortFields on Cohort {
    ... on CompoundCohort {
      ...CompoundCohortFields
    }
    ... on ExpressionCohort {
      ...ExpressionCohortFields
    }
    ... on FunnelCohort {
      ...FunnelCohortFields
    }
    ... on MeasureCohort {
      ...MeasureCohortFields
    }
    ... on PerformedCohort {
      ...PerformedCohortFields
    }
    ... on RetentionCohort {
      ...RetentionCohortFields
    }
  }
  ${CompoundCohortFragment}
  ${ExpressionCohortFragment}
  ${FunnelCohortFragment}
  ${MeasureCohortFragment}
  ${PerformedCohortFragment}
  ${RetentionCohortFragment}
`;

export const CohortRefPropsFragment = gql`
  fragment CohortRefPropsFields on CohortRefProps {
    samplePercentage
    dateRange {
      ...DateRangeFields
    }
  }
  ${DateRangeFieldsFragment}
`;

export const CohortRefFragment = gql`
  fragment CohortRefFields on CohortRef {
    cohort {
      ...CohortFields
    }
    props {
      ...CohortRefPropsFields
    }
  }
  ${CohortFragment}
  ${CohortRefPropsFragment}
`;

export const CohortInspectFieldFieldsFragment = gql`
  fragment CohortInspectFieldFields on Field {
    key
    name
    isFactField
    nestedFieldMetadata {
      key
    }
  }
`;

export const InspectSubjectTableTableColumnFieldsFragment = gql`
  fragment InspectSubjectTableTableColumnFields on InspectSubjectTableTableColumn {
    cells {
      value
    }
    header {
      field {
        key
        name
        isFactField
      }
    }
  }
`;

export const InspectSubjectTableSummaryFieldsFragment = gql`
  fragment InspectSubjectTableSummaryFields on InspectSubjectTableSummary {
    tableSize
    totalCount
  }
`;

export const InspectSubjectTableFieldsFragment = gql`
  fragment InspectSubjectTableFields on InspectSubjectTable {
    columns {
      ...InspectSubjectTableTableColumnFields
    }
    summary {
      ...InspectSubjectTableSummaryFields
    }
  }
  ${InspectSubjectTableTableColumnFieldsFragment}
  ${InspectSubjectTableSummaryFieldsFragment}
`;

export const InspectErrorFragment = gql`
  fragment InspectErrorFields on InspectError {
    message
    status
  }
`;
